import { useState, useEffect } from "react";
import axiosInstance from "../../api/axiosInstance";
import { toast } from "react-toastify";

const MODULE_OPTIONS = [
  { value: "about", label: "About" },
  { value: "facilities", label: "Facilities" },
  { value: "trust-team", label: "Trust Team" },
  { value: "school-team", label: "School Team" },
  { value: "gallery", label: "Gallery" },
  { value: "documents", label: "Documents" },
  { value: "notifications", label: "Notifications" },
  { value: "contact", label: "Contact Us" },
];

const emptyForm = {
  label: "",
  link_type: "module",
  link_value: "",
  parent_id: "",
  display_order: 0,
  open_new_tab: false,
};

function MenuBuilderPage() {
  const [menu, setMenu] = useState([]);
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchMenu();
    fetchPages();
  }, []);

  const fetchMenu = async () => {
    try {
      const res = await axiosInstance.get("/api/menu");
      setMenu(res.data);
    } catch (err) {
      toast.error("Failed to load menu");
    } finally {
      setLoading(false);
    }
  };

  const fetchPages = async () => {
    try {
      const res = await axiosInstance.get("/api/pages");
      setPages(res.data);
    } catch (err) {
      // Non-critical — page-linking just won't have options if this fails
    }
  };

  const openAddModal = (parentId = "") => {
    setEditingId(null);
    setForm({ ...emptyForm, parent_id: parentId });
    setShowModal(true);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => {
      const updated = {
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      };
      // Reset link_value when switching link_type, since the options differ
      if (name === "link_type") updated.link_value = "";
      return updated;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    const payload = {
      label: form.label,
      link_type: form.link_type,
      link_value: form.link_value,
      display_order: Number(form.display_order),
      open_new_tab: form.open_new_tab,
      parent_id: form.parent_id ? Number(form.parent_id) : null,
    };

    try {
      await axiosInstance.post("/api/menu", payload);
      toast.success("Menu item added");
      setShowModal(false);
      fetchMenu();
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to add menu item");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, label) => {
    if (
      !window.confirm(
        `Delete "${label}"? Any submenu items under it will also be deleted.`,
      )
    )
      return;
    try {
      await axiosInstance.delete(`/api/menu/${id}`);
      toast.success("Menu item deleted");
      fetchMenu();
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to delete");
    }
  };

  const renderLinkValueField = () => {
    if (form.link_type === "module") {
      return (
        <select
          className="form-select"
          name="link_value"
          value={form.link_value}
          onChange={handleChange}
          required
        >
          <option value="">Select a module...</option>
          {MODULE_OPTIONS.map((m) => (
            <option key={m.value} value={m.value}>
              {m.label}
            </option>
          ))}
        </select>
      );
    }
    if (form.link_type === "page") {
      return (
        <select
          className="form-select"
          name="link_value"
          value={form.link_value}
          onChange={handleChange}
          required
        >
          <option value="">Select a page...</option>
          {pages.map((p) => (
            <option key={p.id} value={p.slug}>
              {p.title}
            </option>
          ))}
        </select>
      );
    }
    return (
      <input
        type="text"
        className="form-control"
        name="link_value"
        placeholder="https://example.com"
        value={form.link_value}
        onChange={handleChange}
        required
      />
    );
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <div className="spinner-border text-primary" role="status"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4 className="mb-0">Menu Builder</h4>
        <button className="btn btn-primary" onClick={() => openAddModal("")}>
          <i className="bi bi-plus-lg me-1"></i> Add Menu Item
        </button>
      </div>

      <div className="card border-0 shadow-sm">
        <div className="list-group list-group-flush">
          {menu.length === 0 ? (
            <p className="text-muted text-center py-4 mb-0">
              No menu items yet.
            </p>
          ) : (
            menu.map((item) => (
              <div key={item.id}>
                <div className="list-group-item d-flex justify-content-between align-items-center">
                  <div>
                    <i className="bi bi-list me-2 text-muted"></i>
                    <span className="fw-medium">{item.label}</span>
                    <span className="badge bg-light text-dark border ms-2">
                      {item.link_type}
                    </span>
                    <span className="text-muted small ms-2">
                      {item.link_value}
                    </span>
                  </div>
                  <div>
                    <button
                      className="btn btn-sm btn-outline-primary me-1"
                      onClick={() => openAddModal(item.id)}
                      title="Add submenu item"
                    >
                      <i className="bi bi-plus-lg"></i> Submenu
                    </button>
                    <button
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => handleDelete(item.id, item.label)}
                    >
                      <i className="bi bi-trash"></i>
                    </button>
                  </div>
                </div>

                {item.children?.length > 0 && (
                  <div className="ps-5">
                    {item.children.map((child) => (
                      <div
                        key={child.id}
                        className="list-group-item d-flex justify-content-between align-items-center border-start"
                      >
                        <div>
                          <i className="bi bi-arrow-return-right me-2 text-muted"></i>
                          <span>{child.label}</span>
                          <span className="badge bg-light text-dark border ms-2">
                            {child.link_type}
                          </span>
                          <span className="text-muted small ms-2">
                            {child.link_value}
                          </span>
                        </div>
                        <button
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => handleDelete(child.id, child.label)}
                        >
                          <i className="bi bi-trash"></i>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {showModal && (
        <>
          <div className="modal show d-block" tabIndex="-1">
            <div className="modal-dialog">
              <div className="modal-content">
                <form onSubmit={handleSubmit}>
                  <div className="modal-header">
                    <h5 className="modal-title">
                      {form.parent_id ? "Add Submenu Item" : "Add Menu Item"}
                    </h5>
                    <button
                      type="button"
                      className="btn-close"
                      onClick={() => setShowModal(false)}
                    ></button>
                  </div>
                  <div className="modal-body">
                    <div className="mb-3">
                      <label className="form-label">Label</label>
                      <input
                        type="text"
                        className="form-control"
                        name="label"
                        value={form.label}
                        onChange={handleChange}
                        required
                      />
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Link Type</label>
                      <select
                        className="form-select"
                        name="link_type"
                        value={form.link_type}
                        onChange={handleChange}
                      >
                        <option value="module">Website Module</option>
                        <option value="page">Custom Page</option>
                        <option value="external">External URL</option>
                      </select>
                    </div>

                    <div className="mb-3">
                      <label className="form-label">
                        {form.link_type === "module"
                          ? "Module"
                          : form.link_type === "page"
                            ? "Page"
                            : "URL"}
                      </label>
                      {renderLinkValueField()}
                    </div>

                    <div className="row">
                      <div className="col-6 mb-3">
                        <label className="form-label">Display Order</label>
                        <input
                          type="number"
                          className="form-control"
                          name="display_order"
                          value={form.display_order}
                          onChange={handleChange}
                        />
                      </div>
                      <div className="col-6 mb-3 d-flex align-items-end">
                        <div className="form-check">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            id="open_new_tab"
                            name="open_new_tab"
                            checked={form.open_new_tab}
                            onChange={handleChange}
                          />
                          <label
                            className="form-check-label"
                            htmlFor="open_new_tab"
                          >
                            Open in new tab
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => setShowModal(false)}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={saving}
                    >
                      {saving ? "Adding..." : "Add"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
          <div className="modal-backdrop show"></div>
        </>
      )}
    </div>
  );
}

export default MenuBuilderPage;
